




































































































































































































































































































































import {Vue, Component, Prop, Watch} from 'vue-property-decorator'
import moment from 'moment'
import {
  postBatchContractCounters,
  postBatchContractCountersByDapp,
  ContractCounterBatchResponse,
  ContractCounterDappBatchResponse,
} from '@/model/collection/ContractCounterCollection'
import {Contract} from '@/model/resource/Contract'
import {Dapp} from '@/model/resource/Dapp'
import {BlockchainVersion} from '@/model/resource/BlockchainVersion'
import NetworkTag from '@/components/NetworkTag.vue'

type InvocationSortField =
  | 'period-0'
  | 'period-1'
  | 'period-2'
  | 'period-3'
  | 'period-4'
  | 'period-5'
  | 'allTime'

/** Default column: rightmost period bucket (Today in Daily view). */
const DEFAULT_SORT_FIELD: InvocationSortField = 'period-5'

export type InvocationSortChangePayload = {
  field: InvocationSortField
  ascending: boolean
  orderBy: string
  interval: number
}

type TableRow = {
  rowKey: string
  title: string
  hash: string
  hashShort: string
  periodCounts: number[]
  periodValues: string[]
  allTimeCount: number
  allTime: string
  deployment: string
  isDapp: boolean
  detailUrl: string | null
  imageUrl: string | null
  /** `n3` | `x` for [NetworkTag], aligned with invocations network tabs */
  networkTag: string | null
}

/** Daily: enough days for column shift (periodSelected up to 6). */
const INTERVAL_DAILY = 14
/** Weekly: sixth column up to ~76 days ago + periodSelected offset (×7). */
const INTERVAL_WEEKLY = 120
/** Bi-weekly: sixth column up to ~153 days ago + offset (×14). */
const INTERVAL_BIWEEKLY = 220
/** Monthly: up to ~11 months of columns + navigation. */
const INTERVAL_MONTHLY = 400

@Component({
  components: {
    NetworkTag,
  },
})
export default class InvocationScrollableTable extends Vue {
  /** At most 10 contracts; invocation batch loads all rows in one POST. */
  @Prop({type: Array, default: () => []}) contracts!: Contract[]
  /** At most 10 dApps; counters aggregated over all contracts of each dApp. */
  @Prop({type: Array, default: () => []}) dapps!: Dapp[]
  @Prop({type: Boolean, default: false}) isLoadingContracts!: boolean
  @Prop({type: Boolean, default: false}) isLoadingDapps!: boolean
  @Prop({type: String, required: true}) period!: string
  @Prop({type: String, required: true}) groupSelected!: string

  periodSelected = 1
  tableRows: TableRow[] = []
  isLoadingCounters = false
  sortField: InvocationSortField = DEFAULT_SORT_FIELD
  sortAscending = false

  get supportedPeriods(): string[] {
    return ['Daily', 'Weekly', 'Bi-weekly', 'Monthly']
  }

  get isContractMode() {
    return this.groupSelected === 'contract'
  }

  get isDappsMode() {
    return this.groupSelected === 'dApps'
  }

  get isInvocationTablePeriod() {
    return (
      (this.isContractMode || this.isDappsMode) &&
      this.supportedPeriods.includes(this.period)
    )
  }

  get entityCount() {
    return this.isDappsMode ? this.dapps.length : this.contracts.length
  }

  get isLoadingEntities() {
    return this.isDappsMode ? this.isLoadingDapps : this.isLoadingContracts
  }

  get showPeriodNav() {
    return this.isInvocationTablePeriod && this.entityCount !== 0
  }

  get showDataRows() {
    return (
      this.isInvocationTablePeriod &&
      this.entityCount > 0 &&
      !this.isLoadingEntities &&
      !this.isLoadingCounters &&
      this.tableRows.length > 0
    )
  }

  get showPlaceholderRow() {
    return !this.showDataRows
  }

  get placeholderMessage() {
    if (!this.supportedPeriods.includes(this.period)) {
      return 'This period view is not available.'
    }
    if (!this.isContractMode && !this.isDappsMode) {
      return '—'
    }
    if (this.isLoadingEntities) {
      return this.isDappsMode
        ? this.$translate('components.InvocationScrollableTable.loadingDapps')
        : 'Loading contracts…'
    }
    if (this.entityCount === 0) {
      return this.isDappsMode
        ? this.$translate('components.InvocationScrollableTable.noDapps')
        : 'No contracts to show.'
    }
    if (this.isLoadingCounters) {
      return 'Loading invocation counts…'
    }
    if (this.tableRows.length === 0) {
      return 'Could not load invocation counts.'
    }
    return '—'
  }

  @Watch('contracts', {deep: true})
  onContractsChange() {
    this.refreshTableData()
  }

  @Watch('dapps', {deep: true})
  onDappsChange() {
    this.refreshTableData()
  }

  @Watch('isLoadingContracts')
  onIsLoadingContractsChange() {
    if (!this.isLoadingContracts) {
      this.refreshTableData()
    }
  }

  @Watch('isLoadingDapps')
  onIsLoadingDappsChange() {
    if (!this.isLoadingDapps) {
      this.refreshTableData()
    }
  }

  @Watch('period')
  onPeriodChange() {
    this.periodSelected = 1
    this.resetSortToDefault()
    this.emitSortChange()
  }

  @Watch('groupSelected')
  onGroupChange() {
    this.periodSelected = 1
    this.resetSortToDefault()
    this.emitSortChange()
  }

  @Watch('periodSelected')
  onPeriodSelectedChange() {
    this.emitSortChange()
  }

  mounted() {
    this.refreshTableData()
  }

  get firstPeriod() {
    if (this.period === 'Daily') {
      return this.periodSelected === 1
        ? 'Today'
        : this.formatDate(this.getDateDaysAgo(0 + this.periodSelected - 1))
    }
    if (this.period === 'Weekly') {
      return this.weeklyColumnLabel(5)
    }
    if (this.period === 'Bi-weekly') {
      return this.biWeeklyColumnLabel(5)
    }
    return this.monthLabel(0)
  }

  get secondPeriod() {
    if (this.period === 'Daily') {
      return this.periodFormatted(1, 1)
    }
    if (this.period === 'Weekly') {
      return this.weeklyColumnLabel(4)
    }
    if (this.period === 'Bi-weekly') {
      return this.biWeeklyColumnLabel(4)
    }
    return this.monthLabel(1)
  }

  get thirdPeriod() {
    if (this.period === 'Daily') {
      const twoDaysAgo = this.getDateDaysAgo(2 + this.periodSelected - 1)
      return this.formatDate(twoDaysAgo)
    }
    if (this.period === 'Weekly') {
      return this.weeklyColumnLabel(3)
    }
    if (this.period === 'Bi-weekly') {
      return this.biWeeklyColumnLabel(3)
    }
    return this.monthLabel(2)
  }

  get fourthPeriod() {
    if (this.period === 'Daily') {
      const threeDaysAgo = this.getDateDaysAgo(3 + this.periodSelected - 1)
      return this.formatDate(threeDaysAgo)
    }
    if (this.period === 'Weekly') {
      return this.weeklyColumnLabel(2)
    }
    if (this.period === 'Bi-weekly') {
      return this.biWeeklyColumnLabel(2)
    }
    return this.monthLabel(3)
  }

  get fifthPeriod() {
    if (this.period === 'Daily') {
      const fourDaysAgo = this.getDateDaysAgo(4 + this.periodSelected - 1)
      return this.formatDate(fourDaysAgo)
    }
    if (this.period === 'Weekly') {
      return this.weeklyColumnLabel(1)
    }
    if (this.period === 'Bi-weekly') {
      return this.biWeeklyColumnLabel(1)
    }
    return this.monthLabel(4)
  }

  get sixthPeriod() {
    if (this.period === 'Daily') {
      const fiveDaysAgo = this.getDateDaysAgo(5 + this.periodSelected - 1)
      return this.formatDate(fiveDaysAgo)
    }
    if (this.period === 'Weekly') {
      return this.weeklyColumnLabel(0)
    }
    if (this.period === 'Bi-weekly') {
      return this.biWeeklyColumnLabel(0)
    }
    return this.monthLabel(5)
  }

  get leftButtonText() {
    return '<<'
  }

  get rightButtonText() {
    return '>>'
  }

  periodFormatted(days: number, timeframe: number): string {
    const date = this.getDateDaysAgo(
      days + (this.periodSelected - 1) * timeframe
    )
    return this.formatDate(date)
  }

  /**
   * Header for one weekly column. colIdx 0 = sixth (oldest), 5 = first (newest).
   * Bounds match [bucketInvocationCount] for Weekly.
   */
  private weeklyColumnLabel(colIdx: number): string {
    const p = this.periodSelected
    const off = (p - 1) * 7
    const newerDaysAgo = 35 - 7 * colIdx + off
    const olderDaysAgo = 41 - 7 * colIdx + off
    if (colIdx === 5 && newerDaysAgo === 0 && p === 1) {
      return `${this.formatDate(this.getDateDaysAgo(olderDaysAgo))} - Today`
    }
    return `${this.formatDate(
      this.getDateDaysAgo(olderDaysAgo)
    )} - ${this.formatDate(this.getDateDaysAgo(newerDaysAgo))}`
  }

  /**
   * Header for one bi-weekly column; bounds match [bucketInvocationCount] for Bi-weekly.
   */
  private biWeeklyColumnLabel(colIdx: number): string {
    const p = this.periodSelected
    const off = (p - 1) * 14
    const newerDaysAgo = 70 - 14 * colIdx + off
    const olderDaysAgo = 83 - 14 * colIdx + off
    if (colIdx === 5 && newerDaysAgo === 0 && p === 1) {
      return `${this.formatDate(this.getDateDaysAgo(olderDaysAgo))} - Today`
    }
    return `${this.formatDate(
      this.getDateDaysAgo(olderDaysAgo)
    )} - ${this.formatDate(this.getDateDaysAgo(newerDaysAgo))}`
  }

  handleClickLeftButton() {
    if (this.periodSelected < 6) {
      this.periodSelected += 1
    }
  }

  handleClickRightButton() {
    if (this.periodSelected > 1) {
      this.periodSelected -= 1
    }
  }

  copyToClipboard(scriptHash: string) {
    if (!scriptHash) return
    this.$utils.copyToClipboard(scriptHash)
    this.$toast.info('system.info.copiedToClipboard')
  }

  sortHeaderClass(field: InvocationSortField) {
    return [
      'invocation-scrollable-table__sort-header',
      {
        'invocation-scrollable-table__sort-header--active':
          this.sortField === field,
      },
    ]
  }

  sortIcon(field: InvocationSortField) {
    if (this.sortField !== field) return ''
    return this.sortAscending ? '▲' : '▼'
  }

  handleSortClick(field: InvocationSortField) {
    if (this.sortField === field) {
      this.sortAscending = !this.sortAscending
    } else {
      this.sortField = field
      this.sortAscending = false
    }
    this.emitSortChange()
  }

  private resetSortToDefault() {
    this.sortField = DEFAULT_SORT_FIELD
    this.sortAscending = false
  }

  private emitSortChange() {
    const api = this.getSortApiParams()
    const payload: InvocationSortChangePayload = {
      field: this.sortField,
      ascending: this.sortAscending,
      orderBy: api.orderBy,
      interval: api.interval,
    }
    this.$emit('sort-change', payload)
  }

  /** Maps the clicked column to list API `orderBy` + `interval` (server-side sort). */
  private getSortApiParams(): {orderBy: string; interval: number} {
    if (this.sortField === 'allTime') {
      return {orderBy: 'totalTransactionCount', interval: 1}
    }
    const colIdx = Number(this.sortField.replace('period-', ''))
    return {
      orderBy: 'recentTransactionCount',
      interval: this.intervalForSortColumn(colIdx),
    }
  }

  private intervalForSortColumn(colIdx: number): number {
    const p = this.periodSelected
    if (this.period === 'Daily') {
      const daysAgo = 5 - colIdx + (p - 1)
      return Math.max(1, daysAgo + 1)
    }
    if (this.period === 'Weekly') {
      const off = (p - 1) * 7
      const olderDaysAgo = 41 - 7 * colIdx + off
      return olderDaysAgo + 1
    }
    if (this.period === 'Bi-weekly') {
      const off = (p - 1) * 14
      const olderDaysAgo = 83 - 14 * colIdx + off
      return olderDaysAgo + 1
    }
    const monthsBack = p - 1 + (5 - colIdx)
    return (monthsBack + 1) * 31
  }

  private async refreshTableData() {
    if (!this.isInvocationTablePeriod) {
      this.tableRows = []
      this.isLoadingCounters = false
      return
    }

    if (this.isLoadingEntities) {
      this.tableRows = []
      this.isLoadingCounters = false
      return
    }

    if (this.isContractMode) {
      const contracts = this.contracts || []
      if (contracts.length === 0) {
        this.tableRows = []
        return
      }
      this.isLoadingCounters = true
      try {
        const ids = contracts.map(c => c.$id)
        const batch = await postBatchContractCounters({
          idContractFk: ids,
          interval: this.getDailyFetchInterval(),
          isReference: false,
        })
        this.tableRows = contracts.map(c =>
          this.buildRowFromBatch(
            {
              rowKey: `contract-${c.$id}`,
              title: c.title || '—',
              copyValue: c.hash || '',
              displaySecondary: c.hash,
              deploymentAt: c.deploymentDate,
              networkTag: this.networkTagFromBlockchainVersion(
                c.blockchainVersion
              ),
            },
            batch,
            new Set([c.$id])
          )
        )
      } catch (e) {
        this.tableRows = []
        // eslint-disable-next-line no-console
        console.error('Failed to load invocation table rows', e)
      } finally {
        this.isLoadingCounters = false
      }
      return
    }

    if (this.isDappsMode) {
      const dapps = this.dapps || []
      if (dapps.length === 0) {
        this.tableRows = []
        return
      }
      this.isLoadingCounters = true
      try {
        const dappIds = dapps.map(d => d.$id)
        const batch: ContractCounterDappBatchResponse = await postBatchContractCountersByDapp(
          {
            idDappFk: dappIds,
            interval: this.getDailyFetchInterval(),
            isReference: false,
          }
        )
        this.tableRows = dapps.map(d => {
          const slug = d.slug || ''
          return this.buildRowFromDappBatch(
            {
              rowKey: `dapp-${d.$id}`,
              title: d.title || '—',
              copyValue: slug,
              displaySecondary: slug,
              deploymentAt: d.publishedDate,
              imageUrl: d.imageUrl ?? null,
              networkTag: this.networkTagFromBlockchainVersion(
                d.blockchainVersion
              ),
            },
            batch,
            d.$id
          )
        })
      } catch (e) {
        this.tableRows = []
        // eslint-disable-next-line no-console
        console.error('Failed to load invocation dApp rows', e)
      } finally {
        this.isLoadingCounters = false
      }
    }
  }

  /** Same rule as invocations dashboard: Neo X identifiers start with `neox`. */
  private networkTagFromBlockchainVersion(
    bv: BlockchainVersion | null
  ): string | null {
    const ni = bv?.networkIdentifier
    if (!ni) return null
    return ni.toLowerCase().startsWith('neox') ? 'x' : 'n3'
  }

  private buildRowFromBatch(
    meta: {
      rowKey: string
      title: string
      copyValue: string
      displaySecondary: string | null
      deploymentAt: string | null
      networkTag: string | null
    },
    batch: ContractCounterBatchResponse,
    contractIdSet: Set<number>
  ): TableRow {
    const byDay = this.mergeDailyForContractIds(batch.daily, contractIdSet)
    const periodCounts = this.buildPeriodCountsRaw(byDay)
    const periodValues = periodCounts.map(n => this.formatNumber(n))
    const allTimeCount = this.sumTotalsForContractIds(
      batch.totals,
      contractIdSet
    )
    const allTime = this.formatNumber(allTimeCount)
    return {
      rowKey: meta.rowKey,
      title: meta.title,
      hash: meta.copyValue,
      hashShort: this.shortSecondary(meta.displaySecondary),
      periodCounts,
      periodValues,
      allTimeCount,
      allTime,
      deployment: this.formatDeployment(meta.deploymentAt),
      isDapp: false,
      detailUrl: null,
      imageUrl: null,
      networkTag: meta.networkTag,
    }
  }

  private buildRowFromDappBatch(
    meta: {
      rowKey: string
      title: string
      copyValue: string
      displaySecondary: string | null
      deploymentAt: string | null
      imageUrl: string | null
      networkTag: string | null
    },
    batch: ContractCounterDappBatchResponse,
    dappId: number
  ): TableRow {
    const byDay = this.mergeDailyForDappId(batch.daily, dappId)
    const periodCounts = this.buildPeriodCountsRaw(byDay)
    const periodValues = periodCounts.map(n => this.formatNumber(n))
    const allTimeCount = this.sumTotalsForDappId(batch.totals, dappId)
    const allTime = this.formatNumber(allTimeCount)
    return {
      rowKey: meta.rowKey,
      title: meta.title,
      hash: meta.copyValue,
      hashShort: this.shortSecondary(meta.displaySecondary),
      periodCounts,
      periodValues,
      allTimeCount,
      allTime,
      deployment: this.formatDeployment(meta.deploymentAt),
      isDapp: true,
      detailUrl: this.resolveDappDetailUrl(meta.copyValue),
      imageUrl: meta.imageUrl,
      networkTag: meta.networkTag,
    }
  }

  private resolveDappDetailUrl(slug: string): string | null {
    if (!slug) return null
    try {
      return this.$router.resolve({
        name: 'dappDetailedView',
        params: {id: slug},
      }).href
    } catch (e) {
      return `/dapp-detailed/${encodeURIComponent(slug)}`
    }
  }

  private mergeDailyForDappId(
    daily: ContractCounterDappBatchResponse['daily'],
    dappId: number
  ): Map<string, number> {
    const map = new Map<string, number>()
    for (const row of daily) {
      if (row.idDappFk !== dappId) continue
      const key = moment(row.createdAt).format('YYYY-MM-DD')
      map.set(key, (map.get(key) ?? 0) + Number(row.transactionCount ?? 0))
    }
    return map
  }

  private sumTotalsForDappId(
    totals: ContractCounterDappBatchResponse['totals'],
    dappId: number
  ): number {
    for (const t of totals) {
      if (t.idDappFk === dappId) {
        return Number(t.totalTransactionCount ?? 0)
      }
    }
    return 0
  }

  private mergeDailyForContractIds(
    daily: ContractCounterBatchResponse['daily'],
    contractIdSet: Set<number>
  ): Map<string, number> {
    const map = new Map<string, number>()
    for (const row of daily) {
      if (!contractIdSet.has(row.idContractFk)) continue
      const key = moment(row.createdAt).format('YYYY-MM-DD')
      map.set(key, (map.get(key) ?? 0) + Number(row.transactionCount ?? 0))
    }
    return map
  }

  private sumTotalsForContractIds(
    totals: ContractCounterBatchResponse['totals'],
    contractIdSet: Set<number>
  ): number {
    let sum = 0
    for (const t of totals) {
      if (contractIdSet.has(t.idContractFk)) {
        sum += Number(t.totalTransactionCount ?? 0)
      }
    }
    return sum
  }

  private shortSecondary(value: string | null) {
    if (!value) return '—'
    if (value.startsWith('0x')) return this.shortHash(value)
    if (value.length <= 14) return value
    return `${value.slice(0, 8)}…${value.slice(-4)}`
  }

  private getDailyFetchInterval(): number {
    switch (this.period) {
      case 'Daily':
        return INTERVAL_DAILY
      case 'Weekly':
        return INTERVAL_WEEKLY
      case 'Bi-weekly':
        return INTERVAL_BIWEEKLY
      case 'Monthly':
        return INTERVAL_MONTHLY
      default:
        return INTERVAL_DAILY
    }
  }

  /**
   * Table columns are sixth → first (periodValues[0] … [5]).
   * Sums daily counters to match the same ranges as the header labels.
   */
  private buildPeriodCountsRaw(byDay: Map<string, number>): number[] {
    return [0, 1, 2, 3, 4, 5].map(colIdx =>
      this.bucketInvocationCount(byDay, colIdx)
    )
  }

  private bucketInvocationCount(byDay: Map<string, number>, colIdx: number) {
    if (this.period === 'Daily') {
      const daysAgo = 5 - colIdx + (this.periodSelected - 1)
      const key = moment(this.getDateDaysAgo(daysAgo)).format('YYYY-MM-DD')
      return byDay.get(key) ?? 0
    }

    if (this.period === 'Weekly') {
      const off = (this.periodSelected - 1) * 7
      const olderDaysAgo = 41 - 7 * colIdx + off
      const newerDaysAgo = 35 - 7 * colIdx + off
      return this.sumDaysAgoInclusive(byDay, newerDaysAgo, olderDaysAgo)
    }

    if (this.period === 'Bi-weekly') {
      const off = (this.periodSelected - 1) * 14
      const olderDaysAgo = 83 - 14 * colIdx + off
      const newerDaysAgo = 70 - 14 * colIdx + off
      return this.sumDaysAgoInclusive(byDay, newerDaysAgo, olderDaysAgo)
    }

    // Monthly: calendar month, sixth column = oldest displayed month
    const monthsBack = this.periodSelected - 1 + (5 - colIdx)
    const start = moment()
      .startOf('month')
      .subtract(monthsBack, 'months')
    const end = moment(start).endOf('month')
    let sum = 0
    for (
      let d = moment(start).startOf('day');
      d.isSameOrBefore(end, 'day');
      d.add(1, 'day')
    ) {
      sum += byDay.get(d.format('YYYY-MM-DD')) ?? 0
    }
    return sum
  }

  /** Sum daily rows for each calendar day from `newerDaysAgo` through `olderDaysAgo` (inclusive). */
  private sumDaysAgoInclusive(
    byDay: Map<string, number>,
    newerDaysAgo: number,
    olderDaysAgo: number
  ) {
    const lo = Math.min(newerDaysAgo, olderDaysAgo)
    const hi = Math.max(newerDaysAgo, olderDaysAgo)
    let sum = 0
    for (let d = lo; d <= hi; d++) {
      const key = moment(this.getDateDaysAgo(d)).format('YYYY-MM-DD')
      sum += byDay.get(key) ?? 0
    }
    return sum
  }

  private formatNumber(n: number) {
    return this.$filter.decimal(n)
  }

  private shortHash(hash: string | null) {
    if (!hash) return '—'
    return `${hash.slice(0, 6)}...${hash.slice(-4)}`
  }

  private formatDeployment(deploymentDate: string | null) {
    if (!deploymentDate) return '—'
    return moment(deploymentDate).format('MM/DD/YYYY HH:mm')
  }

  private monthLabel(colIndex = 0) {
    const today = new Date()
    const offset = this.periodSelected - 1 + colIndex
    const d = new Date(today.getFullYear(), today.getMonth() - offset, 1)
    const month = d.toLocaleString('en-US', {month: 'long'})
    if (d.getFullYear() < today.getFullYear()) {
      return `${month}/${d.getFullYear()}`
    }
    return month
  }

  private formatDate(date: Date): string {
    return `${date.getMonth() + 1}/${date.getDate()}`
  }

  private getDateDaysAgo(days: number): Date {
    const date = new Date()
    date.setDate(date.getDate() - days)
    return date
  }
}
